import {
  type LDClient,
  type LDContext,
  type LDFlagSet,
  initialize
} from "launchdarkly-js-client-sdk";
import { useCallback, useContext, useEffect, useState } from "react";

import { GlobalContext } from "../global-provider";

const LAUNCH_DARKLY_CLIENT_ID = import.meta.env.VITE_LAUNCH_DARKLY_ID as string;

let flags: LDFlagSet = {};

export const flagCheck = async (flag: string) => {
  await launchDarklyClient;
  return !!flags[flag];
};

const getLDKey = () => {
  const [_, quoteId] = window.location.pathname.split("/");
  return quoteId || "NO_QUOTE_ID";
};

/**
 * Waits for full initialization of the LaunchDarkly client. This will time out
 * after 2 seconds, letting waiting code continue with default flag values.
 * If there's no error, LD client can continue initializing in the background.
 *
 * To track impact in production a `feat_flag_wait` event is sent to MixPanel.
 */
export const launchDarklyClient = new Promise<LDClient>((resolve, reject) => {
  const WAIT_TIMEOUT = 2000;
  const client = initialize(LAUNCH_DARKLY_CLIENT_ID, {
    kind: "user",
    key: getLDKey()
  });
  const timeout = setTimeout(() => {
    resolve(client);
  }, WAIT_TIMEOUT);
  client.on("initialized", () => {
    flags = kebabToCamelcase(client.allFlags());
    clearTimeout(timeout);
    resolve(client);
  });
  client.on("failed", err => {
    clearTimeout(timeout);
    console.error("LaunchDarkly failed to initialize", err);
    reject(new Error("LaunchDarkly failed to initialize"));
  });
});

/**
 * Custom useFlags hook allows for hook overrides in querystring.
 */
export const useFlags = () => {
  const { flagOverrides } = useContext(GlobalContext);
  const [reactFlags, setReactFlags] = useState(flags);

  // Convert flags to camelcase and replace query string overrides.
  const updateFlags = useCallback(
    (client: LDClient) => {
      const ldFlags = kebabToCamelcase(client.allFlags());
      flags = Object.keys(flagOverrides).reduce(
        (accum, flagOverride) => {
          if (flagOverride in accum) {
            accum[flagOverride] = flagOverrides[flagOverride];
          }
          return accum;
        },
        { ...ldFlags }
      );
      setReactFlags(flags);
    },
    [flagOverrides]
  );

  // Get promise-wrapped client, update flags, and listen for changes.
  useEffect(() => {
    void (async () => {
      const client = await launchDarklyClient;
      const handleChange = () => {
        updateFlags(client);
      };
      client.on("change", handleChange);
      updateFlags(client);
      return () => {
        client.off("change", handleChange);
      };
    })();
  }, [updateFlags]);

  return reactFlags;
};

/**
 * Identify user with LaunchDarkly, allowing for targeted flag values.
 */
export async function identify(context: LDContext) {
  try {
    const client = await launchDarklyClient;
    const ctx = {
      ...context
    };
    await client.identify(ctx);
  } catch (e) {
    // if this fails it should just use the previously created UNAUTHORIZED_USER flags
    console.error(e);
  }
}

/**
 * Converts kebab-case flag keys to camelcase.
 */
function kebabToCamelcase(input: LDFlagSet) {
  const output: LDFlagSet = {};
  Object.keys(input).forEach(k => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    output[k.replace(/-./g, m => m.toUpperCase()[1])] = input[k];
  });
  return output;
}
